import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  HStack,
  VStack,
  Divider,
  Select,
  Input,
} from '@chakra-ui/react'
import { useState, useEffect, useMemo } from 'react'
import { Column } from 'react-table'
import { DataTable } from '@/components/DataDisplay/Table/DataTable'
import { RevenueDetailItem, useRevenueDetails } from '../queries/useUserStats'

type Props = {
  isOpen: boolean
  onClose: () => void
  month: number
  packageName: string
}

export default function RevenueDetailsModal({
  isOpen,
  onClose,
  month,
  packageName,
}: Props) {
  const [packageRecurring, setPackageRecurring] = useState<string>('all')
  const [paymentType, setPaymentType] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const {
    data: revenueDetails,
    isLoading,
    refetch,
  } = useRevenueDetails({
    month,
    package: packageName,
    package_recurring:
      packageRecurring !== 'all' ? packageRecurring : undefined,
    payment_type: paymentType !== 'all' ? paymentType : undefined,
    enabled: isOpen,
  })

  useEffect(() => {
    if (isOpen) {
      refetch()
    }
  }, [packageRecurring, paymentType, isOpen, refetch])

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
      setSortOrder('desc')
      setPackageRecurring('all')
      setPaymentType('all')
    }
  }, [isOpen])

  const details = revenueDetails?.details || []

  const filteredDetails = useMemo(() => {
    let result = [...details]

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      result = result.filter((row) => {
        return (
          row.user_email?.toLowerCase().includes(query) ||
          row.company_name?.toLowerCase().includes(query) ||
          row.payment_type?.toLowerCase().includes(query) ||
          row.package?.toLowerCase().includes(query) ||
          row.period?.toLowerCase().includes(query) ||
          row.amount.toString().includes(query)
        )
      })
    }

    result.sort((a, b) => {
      return sortOrder === 'asc' ? a.amount - b.amount : b.amount - a.amount
    })

    return result
  }, [details, searchQuery, sortOrder])

  const filteredTotalAmount = filteredDetails.reduce(
    (sum, d) => sum + (d.amount || 0),
    0,
  )
  const filteredCount = filteredDetails.length

  const columns: Column<RevenueDetailItem>[] = [
    {
      Header: 'User Email',
      accessor: 'user_email',
      disableSortBy: true,
    },
    {
      Header: 'Company Name',
      accessor: 'company_name',
      disableSortBy: true,
    },
    {
      Header: 'Payment Type',
      accessor: 'payment_type',
      disableSortBy: true,
    },
    {
      Header: 'Package',
      accessor: 'package',
      disableSortBy: true,
    },
    {
      Header: 'Period',
      accessor: 'period',
      disableSortBy: true,
    },
    {
      Header: 'Amount (THB)',
      accessor: 'amount',
      disableSortBy: true,
      Cell: ({ value }: { value: number }) => formatCurrency(value),
    },
  ]

  const displayPackage =
    packageName === 'all'
      ? 'All Packages'
      : packageName.charAt(0).toUpperCase() + packageName.slice(1) + ' Package'

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>
          <VStack align="flex-start" spacing={1}>
            <Text fontSize="lg" fontWeight="bold">
              Revenue Details
            </Text>
            <Text fontSize="sm" fontWeight="normal" color="gray.600">
              {revenueDetails?.month} {revenueDetails?.year} - {displayPackage}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} overflowY="auto">
          <Box mb={4}>
            <HStack spacing={4} mb={4}>
              <Input
                w="250px"
                size="sm"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />

              <Select
                w="150px"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                size="sm"
              >
                <option value="desc">Highest Amount</option>
                <option value="asc">Lowest Amount</option>
              </Select>

              <Select
                w="150px"
                value={packageRecurring}
                onChange={(e) => setPackageRecurring(e.target.value)}
                size="sm"
              >
                <option value="all">All Period</option>
                <option value="month">Monthly</option>
                <option value="year">Yearly</option>
              </Select>

              <Select
                w="150px"
                value={paymentType}
                onChange={(e) => setPaymentType(e.target.value)}
                size="sm"
              >
                <option value="all">All Payment</option>
                <option value="credit_card">Credit Card</option>
                <option value="bank_transfer">Bank Transfer</option>
              </Select>
            </HStack>

            <HStack spacing={8} align="center">
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Total Amount
                </Text>
                <Text fontSize="xl" fontWeight="bold" color="green.600">
                  ฿{formatCurrency(filteredTotalAmount)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Total Transactions
                </Text>
                <Text fontSize="xl" fontWeight="bold">
                  {filteredCount}
                </Text>
              </Box>
            </HStack>
          </Box>

          <Divider mb={4} />

          <DataTable<RevenueDetailItem>
            columns={columns}
            data={filteredDetails}
            isLoading={isLoading}
            showPagination
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

function formatCurrency(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}
